import { normalizeGradingFeeMode } from './gradingFeeModes';

export const CATEGORY_SLUGS = Object.freeze({
  All: 'all', Baseball: 'baseball', Basketball: 'basketball', Football: 'football',
  Hockey: 'hockey', Pokemon: 'pokemon', Other: 'other', Vault: 'vault',
  GOAT: 'goat', Trending: 'trending'
});
export const CATEGORY_LABELS = Object.freeze(
  Object.fromEntries(Object.entries(CATEGORY_SLUGS).map(([label, slug]) => [slug, label]))
);
export const RATE_FIELDS = new Set(['card_gem_rate', 'psa9_grade_rate']);

const makeCriteria = ({ category, gradingFeeMode, description = '', additionalFilters = [], sortField = 'coin_score', sortOrder = 'desc', visibleColumns }) => {
  const slug = CATEGORY_SLUGS[category] || (CATEGORY_LABELS[category] ? category : null);
  if (!slug) throw new Error('Choose a supported grading category.');
  const criteria = {
    version: 2,
    category: slug,
    gradingFeeMode: normalizeGradingFeeMode(gradingFeeMode),
    description,
    additionalFilters: additionalFilters.filter(filter => filter.field && filter.type && filter.value !== '' && filter.value !== null && filter.value !== undefined),
    sortField: sortField || 'coin_score',
    sortOrder: sortOrder || 'desc'
  };
  if (visibleColumns) criteria.visibleColumns = visibleColumns;
  return criteria;
};

export const buildGradingCriteria = makeCriteria;

export const displaySavedConditions = (criteria) => {
  if (criteria?.version !== 2) return criteria?.additionalFilters || [];
  return (criteria.additionalFilters || []).map(filter => ({
    ...filter,
    value: RATE_FIELDS.has(filter.field) && typeof filter.value === 'number'
      ? String(filter.value * 100)
      : String(filter.value)
  }));
};

export const criteriaForSavedFilter = (saved, selectedCategory, currentMode) => {
  if (!saved || !saved.criteria || typeof saved.criteria !== 'object' || Array.isArray(saved.criteria)) {
    throw new Error('This saved filter is malformed. Repair or resave it.');
  }
  const raw = saved.criteria;
  if (raw.version === 2) {
    if (!CATEGORY_LABELS[raw.category]) throw new Error('This saved filter has an unsupported category.');
    return { criteria: raw, units: 'canonical', categoryLabel: CATEGORY_LABELS[raw.category] };
  }
  const category = selectedCategory; // Legacy saves never recorded their original view.
  const slug = CATEGORY_SLUGS[category];
  if (!slug) throw new Error('Choose a supported grading category.');
  return {
    criteria: { ...raw, version: 1, category: slug, gradingFeeMode: normalizeGradingFeeMode(raw.gradingFeeMode || currentMode) },
    units: 'display',
    categoryLabel: category
  };
};

const readPage = async (criteria, units, page, expectedDataVersion, signal, fetchImpl) => {
  const response = await fetchImpl('/api/grading/query', {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ criteria, units, page, pageSize: 100, ...(expectedDataVersion ? { expectedDataVersion } : {}) }),
    signal
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || `Grading query failed (${response.status}).`);
  if (!Array.isArray(data.cards) || typeof data.total !== 'number' || !data.data_version) {
    throw new Error('The grading response is incomplete.');
  }
  return data;
};

export const fetchAllGradingCards = async (criteria, { units = 'display', signal, fetchImpl = fetch } = {}) => {
  let first = await readPage(criteria, units, 1, null, signal, fetchImpl);
  const cards = [...first.cards];
  const total = first.total;
  const dataVersion = first.data_version;
  const pages = Math.ceil(total / 100);
  if (pages > 15) throw new Error('The grading category exceeds its published limit.');
  for (let page = 2; page <= pages; page += 1) {
    const next = await readPage(criteria, units, page, dataVersion, signal, fetchImpl);
    if (next.data_version !== dataVersion || next.total !== total) {
      throw new Error('The grading dataset changed. Retry the query.');
    }
    cards.push(...next.cards);
  }
  if (cards.length !== total) throw new Error('The grading result was incomplete. Retry the query.');
  return { ...first, cards };
};
